import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button } from '@mui/material';
import { getCookieLanguage, normalizeLanguage, setStoredLanguage } from '../../utilities/lang';
import { COUNTRY_TO_LANGUAGE, getBrowserCountry } from '../../utilities/geo';

// First-launch language picker. Shows once when:
//   - no dresez_lang cookie yet (the visitor hasn't picked)
//   - resolveSuggestion() yields one of our 7 non-English supported locales
//     by trying three browser-native signals in order:
//       1. navigator.language        (e.g. 'es-ES' → 'es')
//       2. Intl.Locale region        (e.g. 'en-IN' → 'IN' → 'hi')
//       3. OS timezone               (e.g. 'Asia/Kolkata' → 'IN' → 'hi')
//     Signals 2–3 catch the very common "English-default browser in a
//     non-English-language country" case (en-US phone in Mumbai, etc.).
//   - the signed-in user.language isn't already set (don't re-ask on a fresh
//     device for a user who chose in a prior session)
// Once they tap either button, setStoredLanguage writes the cookie and the
// page reloads — same hand-off the Settings LanguageMenu uses. Subsequent
// visits short-circuit on the cookie check and the modal never renders.
//
// Native labels are deliberately hardcoded here rather than translated via
// react-intl: the modal renders before the user has confirmed which catalog
// to load, and we want the *detected* language's catalog (whichever it is)
// rendering its own script regardless of what react-intl decided. Phase C's
// fontFamilyForLocale + Fontsource subset loaders mean the Devanagari and
// Arabic labels below get their proper glyphs before this modal paints.
const NATIVE_LABELS = {
  es: {
    question: '¿Continuar en Español?',
    action: 'Continuar en Español',
  },
  'pt-BR': {
    question: 'Continuar em Português?',
    action: 'Continuar em Português',
  },
  hi: {
    question: 'हिन्दी में जारी रखें?',
    action: 'हिन्दी में जारी रखें',
  },
  ur: {
    question: 'اردو میں جاری رکھیں؟',
    action: 'اردو میں جاری رکھیں',
  },
  fr: {
    question: 'Continuer en Français ?',
    action: 'Continuer en Français',
  },
  de: {
    question: 'Auf Deutsch weiter?',
    action: 'Auf Deutsch weiter',
  },
  id: {
    question: 'Lanjutkan dalam Bahasa Indonesia?',
    action: 'Lanjutkan dalam Bahasa Indonesia',
  },
};

// Resolve a non-English language suggestion: browser language first
// (strongest signal — explicit preference) then country detection via
// getBrowserCountry (region + timezone). Returns one of our 7 non-en
// supported locales, or null when nothing maps. Never returns 'en' — the
// caller treats null and 'en' the same way (no modal).
function resolveSuggestion() {
  const fromBrowser = normalizeLanguage(navigator.language);
  if (fromBrowser && fromBrowser !== 'en') return fromBrowser;

  // Browser yielded 'en' or null — fall back to country detection.
  const country = getBrowserCountry();
  if (!country) return null;
  return COUNTRY_TO_LANGUAGE[country.toUpperCase()] || null;
}

export function LanguageWelcome() {
  const { isLoading, user } = useSelector((state) => state.user);

  // navigator.language / timezone don't change mid-session, so this is stable.
  const detected = useMemo(() => resolveSuggestion(), []);
  const cookieSet = useMemo(() => !!getCookieLanguage(), []);

  // Don't render until user state hydrates — otherwise a signed-in user with
  // a stored language briefly sees the modal before the user.language guard
  // below kicks in on the next render.
  if (isLoading) return null;
  if (cookieSet) return null;
  if (!detected || detected === 'en') return null;
  if (user?.language) return null;

  const native = NATIVE_LABELS[detected];
  if (!native) return null;

  const pick = (code) => {
    setStoredLanguage(code);
    window.location.reload();
  };

  return (
    <Modal
      open
      disableEscapeKeyDown
      className="flex items-center justify-center"
      onClose={() => {}}
    >
      <div className="bg-white dark:bg-[#1c1d22] dark:text-gray-100 rounded-lg p-6 mx-4 w-full max-w-[420px] outline-none">
        <h2 className="text-[20px] font-bold mb-1">Choose your language</h2>
        <p className="text-[15px] text-gray-600 dark:text-gray-300 mb-4">
          {native.question}
        </p>
        <div className="flex flex-col gap-2">
          <Button variant="contained" onClick={() => pick(detected)}>
            {native.action}
          </Button>
          <Button variant="outlined" onClick={() => pick('en')}>
            Continue in English
          </Button>
        </div>
        <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-3">
          You can change this later in Settings.
        </p>
      </div>
    </Modal>
  );
}

export default LanguageWelcome;
